export type AgentState = 'idle' | 'thinking' | 'working' | 'waiting' | 'blocked' | 'done' | 'failed' | 'expired';

export interface BotRow {
  id: string;
  business_id: string;
  name: string;
  label: string;
  role_prompt: string;
  standing_rules: string;
  glyph: string;
  colour: string;
  model: string;
  /** Spending mode. min = GPT-6 Luna, max = GPT-6.1 Sol with subagents. Website bots are always max. */
  mode: 'min' | 'max';
  voice: string;
  sort_order: number;
  is_master: boolean;
  kind: 'specialist' | 'website' | 'marketing' | 'sales';
  /** Switched off by the owner. The marketing and sales managers start this way. */
  paused: boolean;
  /** Marketing or sales manager duties: { duty: { on, mode, min_stars? } }. Empty = defaults. */
  duties: Partial<Record<DutyKey | SalesDutyKey, Duty>> & { first_touch?: FirstTouch };
  activated_at: string | null;
  archived_at: string | null;
  created_at: string;
}

// ── Marketing manager duties ─────────────────────────────────────────────────

export type DutyKey =
  | 'instagram_comments'
  | 'facebook_comments'
  | 'linkedin_comments'
  | 'google_reviews'
  | 'spam_cleanup'
  | 'mentions'
  | 'auto_posts'
  | 'weekly_report';

export type DutyMode = 'auto' | 'approve';

export interface Duty {
  on: boolean;
  mode: DutyMode;
  min_stars?: number;
}

export type Duties = Record<DutyKey, Duty>;

/** Which connection a duty depends on. 'all' needs at least one channel connected. */
export type DutyGroup = 'instagram' | 'facebook' | 'linkedin' | 'google' | 'all';

export interface DutyInfo {
  key: DutyKey;
  title: string;
  does: string;
  hasMode: boolean;
  defaultMode: DutyMode;
  group: DutyGroup;
}

export const DUTY_GROUPS: Record<DutyGroup, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  google: 'Google Business',
  all: 'All channels',
};

/** Order the groups show in. */
export const DUTY_GROUP_ORDER: DutyGroup[] = ['instagram', 'facebook', 'linkedin', 'google', 'all'];

/** True when the channel a group depends on is connected. Unknown (still loading) counts as connected. */
export function groupConnected(group: DutyGroup, connected: ReadonlySet<DutyGroup> | null): boolean {
  if (!connected) return true;
  if (group === 'all') return connected.size > 0;
  return connected.has(group);
}

/** Mirrors supabase/functions/_shared/marketingDuties.ts; the server is the source of truth. */
export const DUTY_CATALOGUE: DutyInfo[] = [
  { key: 'instagram_comments', title: 'Reply to Instagram comments', does: 'Reads every new comment on your Instagram posts and answers real ones in your brand voice.', hasMode: true, defaultMode: 'auto', group: 'instagram' },
  { key: 'mentions', title: 'Watch Instagram mentions', does: 'Tells you when someone mentions your business and whether it is worth resharing.', hasMode: false, defaultMode: 'approve', group: 'instagram' },
  { key: 'facebook_comments', title: 'Reply to Facebook comments', does: 'Reads every new comment on your Facebook posts and answers real ones in your brand voice.', hasMode: true, defaultMode: 'auto', group: 'facebook' },
  { key: 'linkedin_comments', title: 'Reply to LinkedIn comments', does: 'Reads every new comment on your LinkedIn posts and answers real ones in your brand voice.', hasMode: true, defaultMode: 'auto', group: 'linkedin' },
  { key: 'google_reviews', title: 'Reply to Google reviews', does: 'Answers new Google reviews by name. Reviews at or above the star floor go out on their own; anything lower waits for you.', hasMode: true, defaultMode: 'auto', group: 'google' },
  { key: 'spam_cleanup', title: 'Remove spam comments', does: 'Flags bot and scam comments on your posts and removes them.', hasMode: true, defaultMode: 'approve', group: 'all' },
  { key: 'auto_posts', title: 'Keep your channels posting', does: 'Drafts posts for Facebook, Instagram, LinkedIn, and Google Business on a steady schedule, following your brand kit. Staged posts publish on their own.', hasMode: false, defaultMode: 'auto', group: 'all' },
  { key: 'weekly_report', title: 'Weekly marketing report', does: 'Every Monday morning: posts, comments, reviews, and what to do next, in plain numbers.', hasMode: false, defaultMode: 'approve', group: 'all' },
];

export function normalizeDuties(raw: unknown): Duties {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, Partial<Duty> | undefined>;
  const out = {} as Duties;
  for (const info of DUTY_CATALOGUE) {
    const given = src[info.key] || {};
    const duty: Duty = {
      on: typeof given.on === 'boolean' ? given.on : true,
      mode: info.hasMode && (given.mode === 'auto' || given.mode === 'approve') ? given.mode : info.defaultMode,
    };
    if (info.key === 'google_reviews') {
      const stars = Number(given.min_stars);
      duty.min_stars = Number.isFinite(stars) && stars >= 1 && stars <= 5 ? Math.round(stars) : 4;
    }
    out[info.key] = duty;
  }
  return out;
}

export function isMarketingOff(bot: BotRow | null | undefined): boolean {
  return !!bot && bot.kind === 'marketing' && bot.paused !== false;
}

// ── Sales manager ────────────────────────────────────────────────────────────

export type SalesDutyKey = 'new_leads' | 'follow_ups' | 'replies' | 'gone_quiet' | 'weekly_review';
export type FirstTouch = 'auto' | 'call' | 'text' | 'email';

export interface SalesDuty {
  on: boolean;
  mode: DutyMode;
}

export type SalesDuties = Record<SalesDutyKey, SalesDuty> & { first_touch: FirstTouch };

export interface SalesDutyInfo {
  key: SalesDutyKey;
  title: string;
  does: string;
  hasMode: boolean;
  defaultMode: DutyMode;
}

/** Mirrors supabase/functions/_shared/salesDuties.ts; the server is the source of truth. */
export const SALES_DUTY_CATALOGUE: SalesDutyInfo[] = [
  { key: 'new_leads', title: 'Reach out to new leads', does: 'When a lead lands on the board, drafts the first touch on the channel you usually use and lines up the follow-up.', hasMode: true, defaultMode: 'approve' },
  { key: 'follow_ups', title: 'Keep follow-ups moving', does: 'When a follow-up comes due, reads the thread, drafts the next touch, and moves the lead along the pipeline.', hasMode: true, defaultMode: 'approve' },
  { key: 'replies', title: 'Answer replies', does: 'When a lead texts or emails back, drafts the answer and updates the stage.', hasMode: true, defaultMode: 'approve' },
  { key: 'gone_quiet', title: 'Chase leads that went quiet', does: 'Open leads with nothing scheduled and no activity past your CRM quiet limit get one more touch or a suggestion to mark them cold.', hasMode: true, defaultMode: 'approve' },
  { key: 'weekly_review', title: 'Weekly pipeline review', does: 'Every Monday morning: what came in, what closed, what is stuck, and the three leads worth a call today.', hasMode: false, defaultMode: 'approve' },
];

export const FIRST_TOUCH_OPTIONS: { value: FirstTouch; label: string; hint: string }[] = [
  { value: 'auto', label: 'Match how you work', hint: 'Follows the channel the lead reached out on and what you usually do.' },
  { value: 'call', label: 'Call first', hint: 'Talking points instead of a message; you make the call.' },
  { value: 'text', label: 'Text first', hint: 'A short text drafted for you to send.' },
  { value: 'email', label: 'Email first', hint: 'An email drafted for you to send.' },
];

export function normalizeSalesDuties(raw: unknown): SalesDuties {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = {} as SalesDuties;
  for (const info of SALES_DUTY_CATALOGUE) {
    const given = (src[info.key] && typeof src[info.key] === 'object' ? src[info.key] : {}) as Partial<SalesDuty>;
    out[info.key] = {
      on: typeof given.on === 'boolean' ? given.on : true,
      mode: info.hasMode && (given.mode === 'auto' || given.mode === 'approve') ? given.mode : info.defaultMode,
    };
  }
  const touch = src.first_touch;
  out.first_touch = touch === 'call' || touch === 'text' || touch === 'email' ? touch : 'auto';
  return out;
}

export function isSalesOff(bot: BotRow | null | undefined): boolean {
  return !!bot && bot.kind === 'sales' && bot.paused !== false;
}

/** The marketing or sales manager while switched off. */
export function isManagerOff(bot: BotRow | null | undefined): boolean {
  return isMarketingOff(bot) || isSalesOff(bot);
}

export interface ChatRow {
  id: string;
  business_id: string;
  kind: 'direct' | 'group' | 'routine' | 'handoff' | 'team';
  title: string | null;
  lead_bot_id: string | null;
  last_message_at: string | null;
  created_at: string;
  members: string[];
}

export interface SessionRow {
  id: string;
  business_id: string;
  bot_id: string;
  chat_id: string;
  openai_session_id: string;
  status: AgentState;
  activity: string | null;
  /** Model doing the current work, e.g. gpt-6.1-sol; changes per step. */
  model: string | null;
  last_turn_id: string | null;
  updated_at: string;
}

export interface Attachment {
  type: 'image' | 'file';
  name: string;
  path: string;
  mime: string;
  size?: number;
  /** Video facts measured on attach so the bots can check Reel / Story rules. */
  width?: number;
  height?: number;
  duration_seconds?: number;
}

export interface MessageRow {
  id: string;
  business_id: string;
  chat_id: string;
  bot_id: string | null;
  author: 'user' | 'bot' | 'system';
  kind: 'text' | 'image' | 'file' | 'handoff' | 'approval' | 'status' | 'routine' | 'report' | 'map' | 'calendar' | 'seo';
  content: string;
  attachments: Attachment[];
  meta: Record<string, unknown>;
  openai_item_id: string | null;
  turn_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SkillRow {
  id: string;
  bot_id: string;
  name: string;
  instructions: string;
  created_at: string;
}

export interface RoutineRow {
  id: string;
  business_id: string;
  bot_id: string;
  chat_id: string | null;
  name: string;
  prompt: string;
  frequency: 'hourly' | 'daily' | 'weekdays' | 'weekly';
  time_of_day: string;
  day_of_week: number | null;
  timezone: string;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
}

export interface RoutineRunRow {
  id: string;
  routine_id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'completed' | 'failed';
  summary: string | null;
}

export type MemoryKind = 'fact' | 'preference' | 'decision' | 'correction' | 'contact';

export interface MemoryRow {
  id: string;
  business_id: string;
  bot_id: string | null;
  kind: MemoryKind;
  content: string;
  source: 'bot' | 'auto' | 'owner' | 'legacy';
  source_chat_id: string | null;
  created_at: string;
  updated_at: string;
}

export type TriggerEvent =
  | 'order_new' | 'booking_new' | 'booking_cancelled' | 'lead_new'
  | 'review_new' | 'invoice_overdue' | 'stock_low' | 'time_request'
  | 'comment_new' | 'mention_new' | 'follow_up_due' | 'lead_reply';

export const TRIGGER_EVENTS: { event: TriggerEvent; label: string; what: string }[] = [
  { event: 'order_new', label: 'New order', what: 'a customer places an order' },
  { event: 'booking_new', label: 'New booking', what: 'a booking is created' },
  { event: 'booking_cancelled', label: 'Booking cancelled', what: 'a booking is cancelled' },
  { event: 'lead_new', label: 'New lead', what: 'a new lead opens on the CRM board' },
  { event: 'follow_up_due', label: 'Follow-up due', what: "a CRM lead's follow-up date arrives" },
  { event: 'lead_reply', label: 'Lead replied', what: 'an open CRM lead texts or emails back' },
  { event: 'review_new', label: 'New Google review', what: 'a new Google review is posted' },
  { event: 'invoice_overdue', label: 'Invoice overdue', what: 'an invoice passes its due date unpaid' },
  { event: 'stock_low', label: 'Low stock', what: 'a tracked item drops to its reorder point' },
  { event: 'time_request', label: 'Time request', what: 'a team member submits a time request' },
  { event: 'comment_new', label: 'New social comment', what: 'someone comments on a Facebook, Instagram, or LinkedIn post' },
  { event: 'mention_new', label: 'Instagram mention', what: 'the business is mentioned on Instagram' },
];

export interface TriggerRow {
  id: string;
  business_id: string;
  bot_id: string;
  event: TriggerEvent;
  prompt: string;
  enabled: boolean;
  last_checked_at: string;
  last_fired_at: string | null;
  fire_count: number;
  created_at: string;
}

export interface ApprovalRuleRow {
  id: string;
  business_id: string;
  bot_id: string;
  change: string;
  label: string;
  created_at: string;
}

/** A site the owner signed a bot's browser into. Origin and time only. */
export interface SignInRow {
  id: string;
  host: string;
  at: string;
  session_id: string;
}

/** A saved website login. The password is never included. */
export interface VaultLogin {
  id: string;
  host: string;
  account_label: string | null;
  updated_at: string;
}

export type NotifyLevel = 'off' | 'important' | 'all';

export interface AgentSettingsRow {
  business_id: string;
  notify_level: NotifyLevel;
  auto_memory: boolean;
}

export interface ActivityItem {
  id: string;
  at: string;
  bot_id: string | null;
  chat_id: string | null;
  kind: 'tool' | 'approval' | 'routine' | 'notice' | 'trigger';
  title: string;
  detail: string | null;
  state: 'ok' | 'failed' | 'waiting' | 'denied' | 'running';
}

export interface ArtifactRow {
  id: string;
  chat_id: string | null;
  bot_id: string | null;
  message_id: string | null;
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
  storage_path: string;
  created_at: string;
}

export interface LabEvent {
  kind: 'session' | 'synced' | 'delta' | 'text' | 'screen' | 'status' | 'approval' | 'error' | 'done' | 'reconnect';
  sessionId?: string;
  botId?: string;
  itemId?: string;
  text?: string;
  imageUrl?: string;
  state?: AgentState;
  label?: string;
  model?: string;
  requestId?: string;
  approvalType?: string;
  origin?: string;
  reason?: string;
  message?: string;
}

export const GLYPHS = ['triangle', 'circle', 'square', 'diamond', 'hexagon'] as const;
export const COLOURS = ['#f59e0b', '#3b82f6', '#10b981', '#ec4899', '#8b5cf6', '#ef4444', '#14b8a6', '#f97316'] as const;
